import React, { forwardRef, useContext } from 'react';
import type { CSSInterpolation } from '@emotion/serialize';
import { SiteContext } from 'src/context/site-context';
import { RedesignButtonStyle } from 'src/enums/button-style';
import type { ButtonStyle } from 'src/enums/button-style';
import { getNewButtonCss } from 'src/components/facilities/buttonCss';
import type { LinkButtonProps } from 'src/components/facilities/link-button';
import LinkButton from 'src/components/facilities/link-button';
import type { Styling } from 'src/context/styling';

export const createBorderStyling = (styling: Styling, buttonStyle?: string | null) => {
  switch (buttonStyle) {
    case RedesignButtonStyle.WHITE_WITH_PLUM_FONT:
      return {
        'border': `1px solid ${styling.colors.camelot}`,
        '&:focus': {
          border: `1px solid ${styling.colors.camelot}`,
        },
      };
    case RedesignButtonStyle.WHITE_WITH_GREEN_FONT:
    case RedesignButtonStyle.TRANSPARENT:
      return {
        'border': `1px solid ${styling.colors.grayOlive}`,
        '&:focus': {
          border: `1px solid ${styling.colors.grayOlive}`,
        },
      };
    default:
      return null;
  }
};

export type HeaderButtonLinkProps = {
  type: 'link';
  buttonProps: Queries.RedesignButton;
  handleClick?: () => void;
  styles?: CSSInterpolation;
};

type HeaderSimpleButtonProps = {
  type: 'button';
  text: string;
  handleClick: () => void;
  buttonStyle?: RedesignButtonStyle;
  styles?: CSSInterpolation;
};

export type HeaderButtonProps = HeaderButtonLinkProps | HeaderSimpleButtonProps;

const HeaderButton = forwardRef<HTMLElement, HeaderButtonProps>((props, ref) => {
  const { styling } = useContext(SiteContext);

  if (props.type === 'link') {
    return (
      props.buttonProps.showButton && (
        <LinkButton
          {...(props.buttonProps as unknown as LinkButtonProps)}
          buttonStyle={props.buttonProps.buttonStyle as ButtonStyle}
          buttonType="grid-button"
          gtmTrackingId="header-button"
          customButtonStyle={[
            getNewButtonCss(props.buttonProps.buttonStyle as RedesignButtonStyle, styling),
            props.styles,
            createBorderStyling(styling, props.buttonProps.buttonStyle),
          ]}
          iconView={props.buttonProps.buttonIcon}
          handleClick={props.handleClick}
          isRedesignButton
          ref={ref}
        />
      )
    );
  } else {
    return (
      <button
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
        type="button"
        css={[
          getNewButtonCss(props.buttonStyle, styling),
          props.styles,
          createBorderStyling(styling, props.buttonStyle),
        ]}
        onClick={props.handleClick}
      >
        {props.text}
      </button>
    );
  }
});

HeaderButton.displayName = 'HeaderButton';

export default HeaderButton;
